import React, { useLayoutEffect, useRef } from 'react';
import { Alert, Fade, Snackbar, alpha } from '@mui/material';

const SEVERITY_SX = {
  success: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    fontWeight: 500,
    background: alpha('#10b981', 0.14),
    border: '1px solid rgba(52, 211, 153, 0.38)',
    boxShadow: '0 4px 20px rgba(16, 185, 129, 0.14)',
    color: '#6ee7b7',
    '& .MuiAlert-icon': { color: '#34d399' },
    '& .MuiAlert-message': { color: '#6ee7b7' },
  },
  error: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    fontWeight: 500,
    background: alpha('#450a0a', 0.92),
    border: '1px solid rgba(248, 113, 113, 0.35)',
    boxShadow: '0 4px 20px rgba(239, 68, 68, 0.12)',
    color: '#fca5a5',
    '& .MuiAlert-icon': { color: '#f87171' },
    '& .MuiAlert-message': { color: '#fca5a5' },
  },
  warning: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    fontWeight: 500,
    background: alpha('#78350f', 0.5),
    border: '1px solid rgba(251, 191, 36, 0.35)',
    color: '#fcd34d',
    '& .MuiAlert-icon': { color: '#fbbf24' },
    '& .MuiAlert-message': { color: '#fcd34d' },
  },
  info: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    fontWeight: 500,
    background: alpha('#0c4a6e', 0.45),
    border: '1px solid rgba(56, 189, 248, 0.35)',
    color: '#7dd3fc',
    '& .MuiAlert-icon': { color: '#38bdf8' },
    '& .MuiAlert-message': { color: '#7dd3fc' },
  },
};

const FeedbackMessage = ({
  message,
  severity = 'error',
  onClose,
  snackbar = false,
  autoHideDuration = 3000,
  scrollToTopOnSuccess = true,
  sx = {},
}) => {
  const alertRef = useRef(null);

  useLayoutEffect(() => {
    if (!message || severity !== 'success' || snackbar || !scrollToTopOnSuccess) return undefined;
    const frame = window.requestAnimationFrame(() => {
      // Assign scrollTop directly so global `scroll-behavior: smooth` styles
      // cannot delay the banner until after a short redirect timer fires.
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // Also reset a nested scrolling layout, if this form is hosted in one.
      let parent = alertRef.current?.parentElement;
      while (parent) {
        const { overflowY } = window.getComputedStyle(parent);
        if (/(auto|scroll)/.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
          parent.scrollTop = 0;
        }
        parent = parent.parentElement;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [message, severity, snackbar, scrollToTopOnSuccess]);

  if (!message) return null;

  const alertSx = {
    borderRadius: 2,
    ...(SEVERITY_SX[severity] ?? SEVERITY_SX.error),
    ...sx,
  };

  if (snackbar) {
    return (
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={autoHideDuration}
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={severity} onClose={onClose} sx={alertSx}>
          {message}
        </Alert>
      </Snackbar>
    );
  }

  return (
    <Fade in={Boolean(message)}>
      <Alert ref={alertRef} severity={severity} onClose={onClose} sx={{ mb: 3, ...alertSx }}>
        {message}
      </Alert>
    </Fade>
  );
};

export default FeedbackMessage;
